import React, {useState, useMemo} from 'react'
import {PageContainer, SearchBoxContainer} from '../../assets/styles/layout'
import RXRButton from '../RXRButton'
import PageHeader from '../PageHeader'
import ClearableInput from '../ClearableInput'
import {makeStyles} from '@mui/styles'
import {spaceMedium, spaceSmall} from '../../assets/styles/spacing'
import Routes from '../../constants/RouteConstants'
import ToggleButtons from '../ToggleButtons'
import {matchPath, useHistory, useLocation, generatePath} from 'react-router-dom'
import {Grid} from '@mui/material'
import ListBuildingsWithDocumentsTable from './ListBuildingsWithDocumentsTable'
import ListDocumentsTable from './ListDocumentsTable'
import SidePanel from '../SidePanel'
import CreateOrEditDocument from './CreateOrEditDocument'
import GenericFilterMenu from '../GenericFilterMenu'
import RXRIcon from '../RXRIcon'
import {useSelector} from 'react-redux'

function ResidentAppDocuments(props) {
  const classes = useStyles()
  const [filterTerm, setFilterTerm] = useState('')
  const [selectedTagFilters, setSelectedTagFilters] = useState([])
  const history = useHistory()
  const {pathname} = useLocation()

  // Get documents to calculate tag counts
  const documentsLookup = useSelector(state => state.GlobalAdmin.documentsLookup)
  const activeOrganizationId = useSelector(state => state.GlobalAdmin.activeOrganizationId)

  // Calculate all tags and their counts
  const tagFilterMap = useMemo(() => {
    const tagCounts = {}
    Object.values(documentsLookup)
      .filter(d => d.organizationId === activeOrganizationId)
      .forEach(d => {
        const tags = d.documentTags || []
        tags.forEach(tag => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1
        })
      })

    // Convert to filterLabelMap format: { tagName: "tagName (count)" }
    const filterMap = {}
    Object.entries(tagCounts).forEach(([tag, count]) => {
      filterMap[tag] = `${tag} (${count})`
    })
    return filterMap
  }, [documentsLookup, activeOrganizationId])
  const allTags = Object.keys(tagFilterMap)

  const byBuildingMatch = matchPath(pathname, {path: Routes.ADMIN_DASHBOARD_DOCUMENTS_BY_BUILDING})
  const byBuildingCreateMatch = matchPath(pathname, {path: Routes.ADMIN_DASHBOARD_DOCUMENTS_CREATE_BY_BUILDING})
  // byBuilding path uses /buildings where /create or /:documentId would be so we need to make sure it's not that
  const createDocumentMatch = byBuildingMatch ? null : matchPath(pathname, {path: Routes.ADMIN_DASHBOARD_DOCUMENTS_CREATE})
  const viewDocumentMatch =
    byBuildingMatch || createDocumentMatch ? null : matchPath(pathname, {path: Routes.ADMIN_DASHBOARD_DOCUMENTS_VIEW_SINGLE})

  const showByBuilding = byBuildingMatch || byBuildingCreateMatch

  const isCreateMode = createDocumentMatch || byBuildingCreateMatch
  let focusedDocumentId = viewDocumentMatch ? viewDocumentMatch.params.documentId : null

  let TableComponent = null
  if (showByBuilding) {
    TableComponent = RenderListBuildingsWithDocumentsTable
  } else {
    TableComponent = RenderListDocumentsTable
  }

  const closeSidePanel = () => {
    history.push(showByBuilding ? Routes.ADMIN_DASHBOARD_DOCUMENTS_BY_BUILDING : Routes.ADMIN_DASHBOARD_DOCUMENTS)
  }

  return (
    <div style={PageContainer}>
      <PageHeader
        title={'Resident app documents'}
        hideTrademark={true}
        rightControlComponent={
          <RXRButton
            onClick={() => {
              history.push(byBuildingMatch ? Routes.ADMIN_DASHBOARD_DOCUMENTS_CREATE_BY_BUILDING : Routes.ADMIN_DASHBOARD_DOCUMENTS_CREATE)
            }}
          >
            Add new
          </RXRButton>
        }
      >
        <Grid container spacing={3} alignItems="center" style={{marginBottom: spaceMedium}}>
          <Grid item md={6}>
            <div className={classes.searchDocumentsContainer}>
              <ClearableInput value={filterTerm} onChange={setFilterTerm} placeholder={'Search documents'} />
            </div>
          </Grid>
          <Grid item md={6}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: spaceSmall}}>
              {allTags.length > 0 && !showByBuilding && (
                <GenericFilterMenu filterLabelMap={tagFilterMap} onChange={setSelectedTagFilters} selectedFilters={selectedTagFilters}>
                  <RXRButton type={RXRButton.TYPE_SECONDARY}>
                    <RXRIcon icon={RXRIcon.SLIDERS} style={{marginRight: spaceSmall}} />
                    Filters {selectedTagFilters.length > 0 ? `(${selectedTagFilters.length})` : ''}
                  </RXRButton>
                </GenericFilterMenu>
              )}
              <div style={{width: 200}}>
                <ToggleButtons
                  selected={showByBuilding ? 0 : 1}
                  buttonLabels={['Assets', 'Files']}
                  onChange={v => history.push(v === 1 ? Routes.ADMIN_DASHBOARD_DOCUMENTS : Routes.ADMIN_DASHBOARD_DOCUMENTS_BY_BUILDING)}
                  inverseColors={true}
                />
              </div>
            </div>
          </Grid>
        </Grid>
      </PageHeader>
      <div style={{flex: 1}}>
        <TableComponent filterTerm={filterTerm} allTags={allTags} selectedTagFilters={selectedTagFilters} />
      </div>

      <SidePanel
        isOpen={!!(focusedDocumentId || isCreateMode)}
        onClose={closeSidePanel}
        title={focusedDocumentId ? 'Edit document' : 'Create new document'}
      >
        <CreateOrEditDocument documentId={focusedDocumentId} onSave={closeSidePanel} onCancel={closeSidePanel} onDelete={closeSidePanel} />
      </SidePanel>
    </div>
  )
}

const useStyles = makeStyles(theme => ({
  searchDocumentsContainer: {
    ...SearchBoxContainer,
  },
}))

export default ResidentAppDocuments

// -------------------------------------------------------------------------------------------------------
// These are basically just helper components that wrap the main table components to handle navigation

function RenderListBuildingsWithDocumentsTable(props) {
  const history = useHistory()

  return (
    <ListBuildingsWithDocumentsTable
      {...props}
      onClickBuilding={bId => {
        history.push(generatePath(Routes.ADMIN_DASHBOARD_DOCUMENTS_BY_BUILDING_VIEW_SINGLE, {buildingId: bId}))
      }}
    />
  )
}

function RenderListDocumentsTable(props) {
  const history = useHistory()

  return (
    <ListDocumentsTable
      {...props}
      onClickDocument={dId => history.push(generatePath(Routes.ADMIN_DASHBOARD_DOCUMENTS_VIEW_SINGLE, {documentId: dId}))}
    />
  )
}
